// Cron expression → prose, for the Templates table. Most people cannot read
// `0 6 * * 1`, and a schedule nobody can read is a schedule nobody can check.
//
// Deliberately narrow. This describes only the five shapes that cover ordinary
// template schedules and returns `null` for everything else, so the caller
// renders the raw expression verbatim. A confidently WRONG description of when
// something runs is far worse than an expression the reader has to decode:
// ranges, value lists, steps outside the minute field, a constrained month,
// and day-of-month AND day-of-week together all fall back rather than guess.
//
// No timezone appears in any label, on purpose — but the label is not the
// whole story. Schedules fire for real now, and every template carries its
// own IANA `schedule_timezone` defaulting to UTC (epic #1874, docs/14 §5),
// so a bare "Daily at 3:00 AM" reads as local time and is wrong for anyone
// outside that zone. This function is handed the expression alone and never
// the row, so it CANNOT know the zone: it describes the cron SHAPE only.
// Every clock time it returns is wall-clock in the template's own zone, and
// rendering that zone beside the label is the caller's job.

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const WEEKDAY_NAMES: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

/** A whole number in range, or null — a leading zero is fine, `01` is not 1e1. */
function fixedNumber(field: string, min: number, max: number): number | null {
  if (!/^\d{1,2}$/.test(field)) {
    return null;
  }
  const value = Number.parseInt(field, 10);
  return value >= min && value <= max ? value : null;
}

function weekday(field: string): number | null {
  const named = WEEKDAY_NAMES[field.toLocaleLowerCase()];
  if (named !== undefined) {
    return named;
  }
  const numeric = fixedNumber(field, 0, 7);
  // Both 0 and 7 are Sunday in every cron dialect that accepts 7.
  return numeric === null ? null : numeric % 7;
}

// The one step form this helper describes: a step in the minute field. A bare
// `*` there is the same thing with a step of one.
function everyNMinutes(field: string): number | null {
  if (field === "*") {
    return 1;
  }
  const match = /^\*\/(\d{1,2})$/.exec(field);
  if (!match?.[1]) {
    return null;
  }
  const step = Number.parseInt(match[1], 10);
  return step >= 1 && step <= 59 ? step : null;
}

function clockTime(hour: number, minute: number): string {
  const suffix = hour < 12 ? "AM" : "PM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(displayHour)}:${String(minute).padStart(2, "0")} ${suffix}`;
}

/**
 * Prose for a cron expression, or `null` when it cannot be described with
 * confidence — render the raw expression in that case.
 */
export function cronLabel(expression: string): string | null {
  const fields = expression.trim().split(/\s+/);
  if (fields.length !== 5) {
    return null;
  }
  const [minuteField, hourField, domField, monthField, dowField] = fields as [
    string,
    string,
    string,
    string,
    string,
  ];
  // A constrained month is a shape this helper does not describe.
  if (monthField !== "*") {
    return null;
  }
  const isEveryDom = domField === "*";
  const isEveryDow = dowField === "*" || dowField === "?";
  // Cron ORs a constrained day-of-month with a constrained day-of-week; that
  // union is exactly the kind of thing a reader would misread from prose.
  if (!isEveryDom && !isEveryDow) {
    return null;
  }

  const step = everyNMinutes(minuteField);
  if (step !== null && hourField === "*" && isEveryDom && isEveryDow) {
    return step === 1 ? "Every minute" : `Every ${String(step)} minutes`;
  }

  const minute = fixedNumber(minuteField, 0, 59);
  if (minute === null) {
    return null;
  }

  if (hourField === "*" && isEveryDom && isEveryDow) {
    return `Hourly at :${String(minute).padStart(2, "0")}`;
  }

  const hour = fixedNumber(hourField, 0, 23);
  if (hour === null) {
    return null;
  }
  const time = clockTime(hour, minute);

  if (isEveryDom && isEveryDow) {
    return `Daily at ${time}`;
  }
  if (isEveryDom) {
    const day = weekday(dowField);
    return day === null ? null : `Weekly on ${WEEKDAYS[day] ?? ""} at ${time}`;
  }
  const dayOfMonth = fixedNumber(domField, 1, 31);
  return dayOfMonth === null
    ? null
    : `Monthly on day ${String(dayOfMonth)} at ${time}`;
}
